import { memo, useState } from 'react';
import { Stack, Typography, Card, Divider, Box, Container, Button, Checkbox } from '@mui/material';
import CallMadeIcon from '@mui/icons-material/CallMade';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Phone from '@mui/icons-material/Phone';
import { ToggleButton } from '../../Mui/toggle-button';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PersonSpan from 'shared/ui/person-span';
import ManagerSpan from 'shared/ui/manager-span';
import { formatDate, formatDateTime } from 'utils/date-format-helpers';
import { LinkToCompany } from 'shared/ui/links/link-to-company';
import { EventDto } from '@sr/dto';
import { useUpdateEventToggle } from 'features/events/events-actions/event-actions.hook';
import { useSnack } from 'shared/ui/snack';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import MailIcon from '@mui/icons-material/Mail';
import { ViewWazzupModal } from './view-wazzup-modal';
import { ViewEmailModal } from './view-email-modal';
import AudioPlayer from 'entities/audio-player/audio-player';
import { EventTaskType, EventType } from '@sr/dto/dist/src/enums/events';
import { EmailPreview as EmailPreviewDetails, EmailPreviewData } from 'shared/ui/email-preview';

type Props = {
  item: EventDto;
  showCompany?: boolean;
  checked?: boolean;
  onCheckChange?: (checked: boolean) => void;
};

type WazzupData = {
  chatId: string;
  text?: string;
  chatType?: string;
  status?: string;
  contentUri?: string;
};

const EmailPreview = ({ content }: { content: string }) => {
  try {
    const emailData: EmailPreviewData = JSON.parse(content);
    return <EmailPreviewDetails data={emailData} />;
  } catch (e) {
    console.log('Ошибка предпросмотра емейла:', e);
    return <>{content}</>;
  }
};

const WazzupPreview = ({ content }: { content: string }) => {
  try {
    const wazzupData: WazzupData = JSON.parse(content);
    const direction = wazzupData.status === 'inbound' ? 'Входящее сообщение' :
      ['sent', 'delivered', 'read'].includes(wazzupData.status || '') ? 'Исходящее сообщение' : 'Сообщение';
    return (
      <Box>
        <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
          {direction} ({wazzupData.chatType || 'Wazzup'}): {wazzupData.chatId}
        </Typography>
        <br />
        <br />
        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{wazzupData.text}</Typography>
        {wazzupData.contentUri && /^https?:\/\//i.test(wazzupData.contentUri) && (
          <Button component="a" href={wazzupData.contentUri} target="_blank" rel="noopener noreferrer">Открыть вложение</Button>
        )}
      </Box>
    );
  } catch (e) {
    console.log('Ошибка предпросмотра wazzup:', e);
    return <>{content}</>;
  }
};

const EventItem = (props: Props) => {
  const {
    item: {
      id,
      type,
      taskType,
      content,
      manager,
      completed,
      important,
      contactPerson,
      createdDate: createdDateStr,
      dueDate: dueDateStr,
      company,
      emailId,
      wazzupDay,
      wazzupMessageCount,
      wazzupCompanyId,
      atsAudioLink
    },
    checked,
    onCheckChange,
    showCompany = false
  } = props;
  const dueDate = dueDateStr ? new Date(dueDateStr) : null;
  const createdDate = new Date(createdDateStr);
  const { showError } = useSnack();
  const [openWazzupModal, setOpenWazzupModal] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState<boolean>(false);

  const updateEventToggle = useUpdateEventToggle();

  const handleImportantClick = async () => {
    await updateEventToggle({ id, completed, important: !important }).catch((e) => {
      showError('Не получилось: ' + extractNestJsErrorMessage(e));
    });
  };

  const handleDoneClick = async () => {
    await updateEventToggle({ id, completed: !completed, important }).catch((e) => {
      showError('Не получилось: ' + extractNestJsErrorMessage(e));
    });
  };

  return (
    <>
      <Card sx={{ minWidth: 275 }}>
        {showCompany && (
          <Box
            sx={{
              background: (theme) => theme.palette.grey[300],
              p: 1
            }}
          >
            {onCheckChange && <Checkbox
              color="primary"
              checked={!!checked}
              onChange={(e, checked) => {
                onCheckChange?.(checked);
              }}
              inputProps={{ 'aria-label': `Select event ${id}` }}
            />}
            Фирма: {company ? <LinkToCompany company={company} /> : 'Отсутствует'}
          </Box>
        )}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              display: 'flex',
              borderRadius: 1.5,
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.neutral'
            }}
          >
            {getIcon(taskType)}
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 160 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0, color: 'text.secondary' }}>
              {manager && <ManagerSpan person={manager} />}
              {contactPerson && <ArrowForwardIosIcon fontSize="small" />}
              {contactPerson && <PersonSpan person={contactPerson} />}
            </Stack>
            <Box sx={{ mt: 1, mb: 1 }}>
              {dueDate && (
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="subtitle2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Назначенная дата:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                    {formatDate(dueDate)}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>

          <Stack alignItems="flex-end" sx={{ pr: 1 }}>
            <Stack direction='row' spacing={1}>
              {type === EventType.CASE && (
                <>
                  <ToggleButton
                    active={completed!}
                    activeCaption='выполнено'
                    inactiveCaption='выполнить'
                    color='primary'
                    size="small"
                    onClick={handleDoneClick} />
                  <ToggleButton
                    active={!important}
                    activeCaption='важное'
                    inactiveCaption='важное'
                    color='warning'
                    size="small"
                    onClick={handleImportantClick} />
                </>
              )}
              {wazzupDay && <Button variant="outlined" onClick={() => setOpenWazzupModal(true)} endIcon={<WhatsAppIcon />}>Wazzup</Button>}
              {emailId && <Button
                variant="outlined"
                onClick={() => setOpenEmailModal(true)}
                endIcon={<MailIcon />}>
              Email
              </Button>
              }
            </Stack>
            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
              {formatDateTime(createdDate)}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        {atsAudioLink !== undefined && atsAudioLink !== null && atsAudioLink !== '' && <AudioPlayer audioUrl={atsAudioLink} />}
        <Container sx={{ p: 2 }}>
          {wazzupDay ? <Typography>Переписка Wazzup за {wazzupDay.split('-').reverse().join('.')} · Сообщений: {wazzupMessageCount}</Typography> : emailId ? <EmailPreview content={content} /> : taskType === EventTaskType.WAZZUP ? <WazzupPreview content={content} /> : <Box dangerouslySetInnerHTML={{ __html: content }} />}
        </Container>
      </Card>
      {wazzupDay && wazzupCompanyId && openWazzupModal && <ViewWazzupModal companyId={wazzupCompanyId} day={wazzupDay} onClose={() => setOpenWazzupModal(false)} />}
      {emailId && openEmailModal && <ViewEmailModal onClose={() => setOpenEmailModal(false)} open={openEmailModal} id={emailId} />}
    </>
  );
};

export default memo(
  EventItem,
  (prevProps, nextProps) =>
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.important === nextProps.item.important &&
    prevProps.item.completed === nextProps.item.completed &&
    prevProps.item.wazzupCompanyId === nextProps.item.wazzupCompanyId &&
    prevProps.item.wazzupMessageCount === nextProps.item.wazzupMessageCount &&
    prevProps.item.createdDate === nextProps.item.createdDate &&
    prevProps.checked === nextProps.checked
);

function getIcon(taskType: EventTaskType) {
  switch (taskType) {
  case EventTaskType.CALL_IN:
    return (
      <>
        <Phone color="primary" />
        <CallReceivedIcon color="primary" />
      </>
    );
  case EventTaskType.CALL_OUT:
    return (
      <>
        <PhoneEnabledIcon color="primary" />
        <CallMadeIcon color="primary" />
      </>
    );
  case EventTaskType.EMAIL_IN:
    return (
      <>
        <MailOutlineIcon color="primary" />
        <CallReceivedIcon color="primary" />
      </>
    );
  case EventTaskType.EMAIL_OUT:
    return (
      <>
        <MailOutlineIcon color="primary" />
        <CallMadeIcon color="primary" />
      </>
    );
  case EventTaskType.MEETING:
    return <Diversity3Icon color="primary" />;
  case EventTaskType.WAZZUP:
    return <WhatsAppIcon color="primary" />;
  }
}
